/**
 * Audio Service for Tripitaka Editor
 * 
 * Handles communication with the backend API for:
 * - Generating audio for PDF pages
 * - Streaming audio from S3
 * - Managing audio playback state
 */

export interface AudioMetadata {
  audioUrl: string;
  duration?: number;
  s3Key?: string;
}

export interface DocumentInfo {
  documentId: string;
  title: string;
  totalPages: number;
  availableAudioPages: number[];
}

class AudioService {
  private baseUrl: string;

  constructor() {
    // Use CloudFront domain for S3 access, fallback to localhost for development
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  }

  /**
   * Generate audio for a specific page of a document
   */
  async generatePageAudio(documentId: string, pageNumber: number, uploadToS3: boolean = true): Promise<AudioMetadata> {
    try {
      const response = await fetch(
        `${this.baseUrl}/v1/documents/${documentId}/pages/${pageNumber}/audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ upload_to_s3: uploadToS3 })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate audio: ${response.statusText}`);
      }

      // Check if response is JSON (S3 URL) or binary (direct audio)
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('application/json')) {
        // S3 URL response
        const data = await response.json();
        return {
          audioUrl: data.audio_url,
          duration: data.duration,
          s3Key: data.s3_key
        };
      } else {
        // Direct audio response
        const blob = await response.blob();
        const audioUrl = URL.createObjectURL(blob);
        return { audioUrl };
      }
    } catch (error) {
      console.error('Error generating page audio:', error);
      throw error;
    }
  }

  /**
   * Get or stream audio for a specific page via CloudFront
   */
  async getPageAudio(documentId: string, pageNumber: number): Promise<string> {
    try {
      // Use CloudFront URL that routes to audio bucket with cache-busting
      const timestamp = Date.now();
      const audioUrl = `${window.location.origin}/audio/documents/${documentId}/pages/${pageNumber}/audio.mp3?v=${timestamp}`;
      
      // Don't check if exists, just return the URL for audio element to try
      return audioUrl;
    } catch (error) {
      console.error('Error getting page audio:', error);
      throw error;
    }
  }

  /**
   * Get document information
   */
  async getDocumentInfo(documentId: string): Promise<DocumentInfo> {
    try {
      const response = await fetch(`${this.baseUrl}/v1/documents/${documentId}/info`);

      if (!response.ok) {
        throw new Error(`Failed to get document info: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting document info:', error);
      throw error;
    }
  }

  /**
   * Original TTS endpoint for direct text synthesis
   */
  async synthesizeText(text: string, voice?: string, speed?: number): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/v1/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, voice, speed })
      });

      if (!response.ok) {
        throw new Error(`TTS failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error synthesizing text:', error);
      throw error;
    }
  }

  /**
   * Check if audio exists for a page via CloudFront (quick check without generating)
   */
  async checkPageAudioExists(documentId: string, pageNumber: number): Promise<boolean> {
    try {
      const audioUrl = `${window.location.origin}/audio/documents/${documentId}/pages/${pageNumber}/audio.mp3`;
      const response = await fetch(audioUrl, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Health check - for S3-based setup, always healthy since we're using CloudFront
   */
  async healthCheck(): Promise<boolean> {
    // For S3-only setup via CloudFront, always return true
    return true;
  }
}

// Export singleton instance
export const audioService = new AudioService();
export default audioService;

// Ensure this is treated as a module
export {};